import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { style } from './product-downloads.style.js'
import { ProductDownloads } from 'components/discovery/pdp/product-downloads/typing/product-downloads-types.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import { HeadingSlotMixin } from 'components/shared/heading/heading-slot-mixin.js'

initializeIconsByRole({
  'csc-product-downloads.pdf': { name: 'file-pdf', style: 'light' },
  'csc-product-downloads.txt': { name: 'file-lines', style: 'light' },
  'csc-product-downloads.link': { name: 'paperclip', style: 'light' },
  'csc-product-downloads.image': { name: 'file-image', style: 'light' },
  'csc-product-downloads.csv': { name: 'file-csv', style: 'light' },
  'csc-product-downloads.spreadsheet': { name: 'file-spreadsheet', style: 'light' },
  'csc-product-downloads.vector': { name: 'file-vector', style: 'light' },
  'csc-product-downloads.video': { name: 'file-video', style: 'light' },
  'csc-product-downloads.audio': { name: 'file-audio', style: 'light' },
  'csc-product-downloads.zip': { name: 'file-zip', style: 'light' },
  'csc-product-downloads.technical': { name: 'cube', style: 'light' }
})

@customElement('csc-product-downloads')
export class CSCProductDownloads extends HeadingSlotMixin(LitElement) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(PDP_PRODUCT_STORE)
  private store!: PdpProductStore

  @property({ type: Object, attribute: 'product-downloads' })
  productDownloads: ProductDownloads = { assets: [] }

  constructor() {
    super()
    createEffect(() => {
      const product = this.store.product()
      if (product) this.productDownloads = product as ProductDownloads
    })
  }


  private getAssetIconRole(asset: string): string {
    const path = asset.split('?')[0] ?? ''
    if (path.endsWith('.pdf')) return 'csc-product-downloads.pdf'
    if (path.endsWith('.jpeg') || path.endsWith('.png') || path.endsWith('.webp')) return 'csc-product-downloads.image'
    if (path.endsWith('.csv')) return 'csc-product-downloads.csv'
    if (path.endsWith('.xlsx') || path.endsWith('.xml')) return 'csc-product-downloads.spreadsheet'
    if (path.endsWith('.docx') || path.endsWith('.txt')) return 'csc-product-downloads.txt'
    if (path.endsWith('.svg') || path.endsWith('.ai')) return 'csc-product-downloads.vector'
    if (path.endsWith('.mp4') || path.endsWith('.mov')) return 'csc-product-downloads.video'
    if (path.endsWith('.mp3')) return 'csc-product-downloads.audio'
    if (path.endsWith('.zip') || path.endsWith('.rar')) return 'csc-product-downloads.zip'
    if (path.endsWith('.stl') || path.endsWith('.obj') || path.endsWith('.dwg')) return 'csc-product-downloads.technical'
    return 'csc-product-downloads.link'
  }

  override render() {
    const urlAssets = (this.productDownloads.assets ?? [])
      .filter((asset): asset is typeof asset & { asset: string } => asset.type === 'url' && !!asset.asset)
    
    if (!urlAssets.length) return nothing
    return html`
      ${this.renderHeadingSlot()}
      <cse-list>
        ${urlAssets.map(asset => html`
          <cse-list-item>
            <cse-link
              url="${asset.asset}"
              target="_blank">
              <cse-icon slot="prefix" icon-role="${this.getAssetIconRole(asset.asset)}"></cse-icon>
              ${asset.name}
            </cse-link>
          </cse-list-item>
        `)}
      </cse-list>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-product-downloads': CSCProductDownloads
  }
}
